import '@/assets/assistant-ui.css';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ThreadMessageLike } from '@assistant-ui/react';
import type { ReadonlyJSONObject } from 'assistant-stream/utils';
import { Button, Card } from 'react-bootstrap';
import { MessageSquare, Plus } from 'lucide-react';
import { Thread } from '@/components/assistant-ui/thread';
import { useAppContext } from '@/Main';
import { cn } from '@/lib/utils';
import {
  toMcpApiErrorMessage,
  useLazyGetMcpThreadQuery
} from '../api/mcpApi';
import type {
  McpActiveChatRun,
  McpThreadMessage
} from '../types/mcpApiTypes';
import { McpRuntimeProvider } from '../McpRuntimeProvider';
import { McpChatHeader } from '../components/McpChatHeader';
import { McpChatHistorySidebar } from '../components/McpChatHistorySidebar';
import { McpChatSuggestions } from '../components/McpChatSuggestions';
import {
  McpFollowupSuggestions,
  McpMessageFollowups
} from '../components/McpFollowupSuggestions';
import { McpComposerAddons } from '../components/McpComposerAddons';
import { McpComposerPlusMenu } from '../components/McpComposerPlusMenu';
import { McpComposerBeforeSend, McpComposerFooter } from '../components/McpContextBudgetRing';
import { McpComposerDomainBadges } from '../components/McpComposerDomainBadges';
import {
  McpComposerMentionsProvider
} from '../components/McpComposerMentionsContext';
import { McpExportPdfAction } from '../components/McpExportPdfAction';
import { McpAssistantText } from '../components/McpAutoBudgetAlert';
import {
  readChatError,
  type McpChatError
} from '../components/McpChatErrorAlert';
import { McpAutoSlowHint } from '../components/McpAutoSlowHint';
import { McpChatModelProvider } from '../components/McpChatModelContext';
import { isMcpHumanToolName } from '../components/mcpChatToolkit';
import { McpToolFallback } from '../components/McpToolFallback';
import { McpToolGroup } from '../components/McpToolGroup';
import { McpToolDisplayNamesProvider } from '../components/McpToolDisplayNames';
import {
  McpHelpModeProvider,
  useMcpHelpMode
} from '../components/McpHelpModeContext';
import {
  isHelpThreadMessages,
  McpHelpChat,
  toHelpMessages
} from '../components/McpHelpChat';
import { McpChatThreadPreload } from '../components/McpChatThreadPreload';
import { McpChatOnboardingInteractionGuard } from '../components/onboarding/McpChatOnboardingInteractionGuard';
import { useMcpChatOnboarding } from '../hooks/McpChatOnboardingContext';
import { useStartNewMcpChat } from '../hooks/useStartNewMcpChat';
import { invalidateMcpThreadHistory } from '../utils/threadHistoryEvents';
import { dedupePresentChartTools } from '../utils/presentChartDedupe';

function feedbackRatingToUi(
  rating: McpThreadMessage['myFeedback']
): 'good' | 'bad' | null {
  if (!rating) return null;
  if (rating.rating === 'positive') return 'good';
  if (rating.rating === 'negative') return 'bad';
  return null;
}

type PersistedToolNote = {
  toolCallId?: string;
  toolName?: string;
  args?: ReadonlyJSONObject;
  resultText?: string;
  isError?: boolean;
};

function asJsonObject(value: unknown): ReadonlyJSONObject {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as ReadonlyJSONObject;
  }
  return {};
}

function toolPartsFromMessage(
  message: McpThreadMessage
): Exclude<ThreadMessageLike['content'], string> {
  const toolParts: Array<{
    type: 'tool-call';
    toolCallId: string;
    toolName: string;
    args: ReadonlyJSONObject;
    argsText: string;
    result?: string;
    isError?: boolean;
  }> = [];

  const rawTools = message.parts?.tools;
  if (Array.isArray(rawTools)) {
    rawTools.forEach((raw, index) => {
      if (!raw || typeof raw !== 'object') return;
      const note = raw as PersistedToolNote;
      if (!note.toolName) return;
      const args = asJsonObject(note.args);
      toolParts.push({
        type: 'tool-call',
        toolCallId: note.toolCallId || `${message.id}-tool-${index}`,
        toolName: note.toolName,
        args,
        argsText: JSON.stringify(args),
        result: note.resultText,
        isError: Boolean(note.isError)
      });
    });
  }

  const uniqueToolParts = dedupePresentChartTools(toolParts);

  const text = (message.content ?? '').trim();
  // A failed turn stores its error as the reply text too; the alert shows it.
  const errorText = chatErrorFromMessage(message)?.message;
  const textParts: Array<{ type: 'text'; text: string }> =
    text && text !== errorText ? [{ type: 'text', text }] : [];

  const isSkillConfigure = uniqueToolParts.some((part) =>
    isMcpHumanToolName(part.toolName)
  );

  return isSkillConfigure
    ? [...textParts, ...uniqueToolParts]
    : [...uniqueToolParts, ...textParts];
}

function chatErrorFromMessage(
  message: McpThreadMessage
): McpChatError | null {
  return message.role === 'assistant' ? readChatError(message.parts?.error) : null;
}

function warningsFromMessage(
  message: McpThreadMessage
): Array<{ code: string; message: string }> {
  const raw = message.parts?.warnings;
  if (!Array.isArray(raw)) return [];
  const out: Array<{ code: string; message: string }> = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const code = typeof row.code === 'string' ? row.code.trim() : '';
    const text = typeof row.message === 'string' ? row.message.trim() : '';
    if (!code || !text) continue;
    out.push({ code, message: text });
  }
  return out;
}

function toThreadMessages(messages: McpThreadMessage[]): ThreadMessageLike[] {
  return messages
    .filter(m => m.role === 'user' || m.role === 'assistant')
    .map(m => {
      const content = toolPartsFromMessage(m);
      const chatError = chatErrorFromMessage(m);
      if (
        !chatError &&
        (!content || (Array.isArray(content) && content.length === 0))
      ) {
        return null;
      }
      const feedbackRating = feedbackRatingToUi(m.myFeedback ?? null);
      const userApiKeyWarnings =
        m.role === 'assistant' ? warningsFromMessage(m) : [];
      const createdAt = m.createdAt ? new Date(m.createdAt) : undefined;
      return {
        id: m.id,
        role: m.role as 'user' | 'assistant',
        content,
        ...(createdAt && !Number.isNaN(createdAt.getTime()) ? { createdAt } : {}),
        ...(m.role === 'assistant'
          ? {
              metadata: {
                custom: {
                  serverMessageId: m.id,
                  ...(feedbackRating ? { feedbackRating } : {}),
                  ...(userApiKeyWarnings.length
                    ? { userApiKeyWarnings }
                    : {}),
                  ...(chatError ? { chatError } : {})
                }
              }
            }
          : {})
      };
    })
    .filter((m): m is NonNullable<typeof m> => m != null);
}

function threadIdFromPath(pathname: string): string | undefined {
  const match = pathname.match(/^\/chat\/c\/([^/]+)\/?$/);
  return match?.[1];
}

type McpLocationState = {
  mcpNewChat?: number;
  initialPrompt?: string;
  promptTemplateId?: string;
  promptTemplateTitle?: string;
};

export default function McpChatPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const startNewChat = useStartNewMcpChat();
  const {
    config: { isDark }
  } = useAppContext();

  const threadId = threadIdFromPath(location.pathname);
  const locationState = (location.state ?? {}) as McpLocationState;

  const [sessionKey, setSessionKey] = useState(() =>
    threadId
      ? `thread-${threadId}`
      : `new-${locationState.mcpNewChat ?? Date.now()}`
  );
  const [historyOpen, setHistoryOpen] = useState(false);
  const [initialMessages, setInitialMessages] = useState<ThreadMessageLike[]>(
    []
  );
  const [rawMessages, setRawMessages] = useState<McpThreadMessage[]>([]);
  const [activeRun, setActiveRun] = useState<McpActiveChatRun | null>(null);
  const [autoSendPrompt, setAutoSendPrompt] = useState<string | null>(() =>
    typeof locationState.initialPrompt === 'string' &&
    locationState.initialPrompt.trim()
      ? locationState.initialPrompt.trim()
      : null
  );
  const [ready, setReady] = useState(!threadId);
  const [error, setError] = useState<string | null>(null);

  const liveCreatedThreadRef = useRef<string | null>(null);
  const loadedThreadRef = useRef<string | null>(null);
  const [fetchThread] = useLazyGetMcpThreadQuery();

  // Explicit "new chat" click — reset session without aborting mid-flight later.
  useEffect(() => {
    if (locationState.mcpNewChat == null) return;
    liveCreatedThreadRef.current = null;
    loadedThreadRef.current = null;
    setSessionKey(`new-${locationState.mcpNewChat}`);
    setInitialMessages([]);
    setRawMessages([]);
    setActiveRun(null);
    setAutoSendPrompt(
      typeof locationState.initialPrompt === 'string' &&
        locationState.initialPrompt.trim()
        ? locationState.initialPrompt.trim()
        : null
    );
    setReady(true);
    setError(null);
  }, [locationState.mcpNewChat, locationState.initialPrompt]);

  // Opening a history thread (not the one just created in this session).
  useEffect(() => {
    if (!threadId) {
      if (loadedThreadRef.current || sessionKey.startsWith('thread-')) {
        liveCreatedThreadRef.current = null;
        loadedThreadRef.current = null;
        setSessionKey(`new-${Date.now()}`);
        setInitialMessages([]);
        setRawMessages([]);
        setActiveRun(null);
        setReady(true);
        setError(null);
      }
      return;
    }
    if (liveCreatedThreadRef.current === threadId) {
      setReady(true);
      setError(null);
      return;
    }
    // Left the chat created in this session: coming back must load it from
    // the server (its reply may still be generating there).
    liveCreatedThreadRef.current = null;

    setSessionKey(`thread-${threadId}`);

    if (loadedThreadRef.current === threadId) {
      setReady(true);
      return;
    }

    let cancelled = false;
    setReady(false);
    setError(null);
    fetchThread(threadId)
      .unwrap()
      .then(detail => {
        if (cancelled) return;
        setRawMessages(detail.messages);
        setInitialMessages(toThreadMessages(detail.messages));
        setActiveRun(detail.activeRun ?? null);
        loadedThreadRef.current = threadId;
        setReady(true);
      })
      .catch(err => {
        if (cancelled) return;
        setError(toMcpApiErrorMessage(err, 'گفتگو پیدا نشد'));
        setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [threadId, fetchThread]);

  if (!ready) {
    return <McpChatThreadPreload isDark={isDark} />;
  }

  if (error) {
    return (
      <Card className="mb-3">
        <Card.Body className="text-center py-5 px-3">
          <div className="d-inline-flex align-items-center justify-content-center text-500 mb-3">
            <MessageSquare size={22} strokeWidth={1.75} />
          </div>
          <h5 className="fs-0 mb-2">گفتگو پیدا نشد</h5>
          <p className="mb-3 fs--1 text-600 mx-auto" style={{ maxWidth: 340 }}>
            این گفتگو وجود ندارد، حذف شده، یا به آن دسترسی ندارید. می‌توانید یک چت
            جدید شروع کنید.
          </p>
          <Button
            size="sm"
            variant="primary"
            onClick={() => startNewChat()}
          >
            <Plus size={15} strokeWidth={2.25} className="me-1" />
            شروع گفتگو
          </Button>
        </Card.Body>
      </Card>
    );
  }

  const useEmptyInitial =
    !threadId || liveCreatedThreadRef.current === threadId;

  return (
    <McpChatModelProvider>
      <McpHelpModeProvider>
        <McpChatPageBody
          sessionKey={sessionKey}
          threadId={threadId}
          useEmptyInitial={useEmptyInitial}
          initialMessages={initialMessages}
          activeRun={useEmptyInitial ? null : activeRun}
          rawMessages={useEmptyInitial ? [] : rawMessages}
          autoSendPrompt={autoSendPrompt}
          historyOpen={historyOpen}
          setHistoryOpen={setHistoryOpen}
          isDark={isDark}
          onThreadCreated={id => {
            liveCreatedThreadRef.current = id;
            invalidateMcpThreadHistory();
          }}
        />
      </McpHelpModeProvider>
    </McpChatModelProvider>
  );
}

type McpChatPageBodyProps = {
  sessionKey: string;
  threadId: string | undefined;
  useEmptyInitial: boolean;
  initialMessages: ThreadMessageLike[];
  activeRun: McpActiveChatRun | null;
  rawMessages: McpThreadMessage[];
  autoSendPrompt: string | null;
  historyOpen: boolean;
  setHistoryOpen: Dispatch<SetStateAction<boolean>>;
  isDark: boolean;
  onThreadCreated: (id: string) => void;
};

function McpChatPageBody({
  sessionKey,
  threadId,
  useEmptyInitial,
  initialMessages,
  activeRun,
  rawMessages,
  autoSendPrompt,
  historyOpen,
  setHistoryOpen,
  isDark,
  onThreadCreated
}: McpChatPageBodyProps) {
  const { helpMode, enterHelpMode, exitHelpMode } = useMcpHelpMode();
  const { needsOnboarding } = useMcpChatOnboarding();
  const isHelpThread = isHelpThreadMessages(rawMessages);
  const showHelp = helpMode || isHelpThread;
  const [fullscreen, setFullscreen] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);

  const toggleFullscreen = useCallback(async () => {
    const el = pageRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen();
        setFullscreen(true);
      } else {
        await document.exitFullscreen();
        setFullscreen(false);
      }
    } catch {
      setFullscreen(prev => !prev);
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      setFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    if (!fullscreen || document.fullscreenElement) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFullscreen(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [fullscreen]);

  useEffect(() => {
    if (isHelpThread) {
      enterHelpMode();
      return;
    }
    // Hydrated a non-help history thread — leave help mode.
    if (rawMessages.length > 0) {
      exitHelpMode();
    }
  }, [isHelpThread, rawMessages.length, enterHelpMode, exitHelpMode]);

  return (
    <McpRuntimeProvider
      key={sessionKey}
      threadId={threadId}
      initialMessages={useEmptyInitial ? [] : initialMessages}
      activeRun={useEmptyInitial ? null : activeRun}
      autoSendPrompt={useEmptyInitial ? autoSendPrompt : null}
      onThreadCreated={onThreadCreated}
      chatOnboardingGuardEnabled={!showHelp}
    >
      <McpChatOnboardingInteractionGuard enabled={!showHelp} />
      <McpToolDisplayNamesProvider>
      <McpComposerMentionsProvider>
      <div
        ref={pageRef}
        className={cn(
          'mcp-chat-page aui-root',
          fullscreen && 'is-fullscreen',
          isDark && 'dark'
        )}
        data-theme={isDark ? 'dark' : 'light'}
      >
        <McpChatHistorySidebar
          open={historyOpen}
          onToggle={() => setHistoryOpen(prev => !prev)}
        />
        <div className="mcp-chat-main">
          <McpChatHeader
            historyOpen={historyOpen}
            onToggleHistory={() => setHistoryOpen(prev => !prev)}
            fullscreen={fullscreen}
            onToggleFullscreen={toggleFullscreen}
          />
          <div className="mcp-chat-thread">
            {showHelp ? (
              <McpHelpChat
                key={sessionKey}
                threadId={threadId}
                initialMessages={
                  useEmptyInitial ? [] : toHelpMessages(rawMessages)
                }
                onThreadCreated={onThreadCreated}
              />
            ) : (
              <Thread
                composerAutoFocus={!needsOnboarding}
                components={{
                  Suggestions: McpChatSuggestions,
                  FollowupSuggestions: McpFollowupSuggestions,
                  MessageFollowups: McpMessageFollowups,
                  ComposerAddon: McpComposerAddons,
                  ComposerMentions: McpComposerDomainBadges,
                  ComposerLeadingAction: McpComposerPlusMenu,
                  ComposerBeforeSend: McpComposerBeforeSend,
                  ComposerFooter: McpComposerFooter,
                  AssistantActionExtra: McpExportPdfAction,
                  AssistantText: McpAssistantText,
                  ThinkingHint: McpAutoSlowHint,
                  ToolFallback: McpToolFallback,
                  ToolGroup: McpToolGroup
                }}
              />
            )}
          </div>
        </div>
      </div>
      </McpComposerMentionsProvider>
      </McpToolDisplayNamesProvider>
    </McpRuntimeProvider>
  );
}
